import { createCustomerIfNotExist, getOrderHistory } from '@/api/customer-api';
import { toast } from '@/hooks/use-toast';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { Customer } from '@/types/customer-type';
import { Participant } from '@/types/fb-type';
import { TOrderWithRelation } from '@/types/order-type';
import { useCallback, useEffect, useRef, useState } from 'react';

export function useChatCustomerOrders(participant?: Participant) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [orders, setOrders] = useState<TOrderWithRelation[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState(false);
  const request = useRef(0);

  // Participant objects change on message events; resolve only when identity changes.
  useEffect(() => {
    let cancelled = false;
    setCustomer(null);
    if (!participant?.id) return;
    createCustomerIfNotExist(participant)
      .then((res) => {
        if (cancelled) return;
        if (!res.success) throw new Error(res.message);
        setCustomer(res.data);
      })
      .catch((error) => {
        if (!cancelled) toast({ title: 'Error', description: error.message || 'Unable to load customer.', variant: 'destructive' });
      });
    return () => { cancelled = true; };
  }, [participant?.id, participant?.conversationId]);

  const refreshOrders = useCallback(async () => {
    if (!customer) return;
    const seq = ++request.current;
    setLoadingOrders(true);
    setOrdersError(false);
    try {
      const res = await getOrderHistory(customer);
      if (seq !== request.current) return;
      if (!res.success) throw new Error(res.message);
      const sorted = [...(res.data.orders ?? [])].sort((a, b) => {
        const timeA = Date.parse(a.order.createdAt ?? '') || 0;
        const timeB = Date.parse(b.order.createdAt ?? '') || 0;
        return timeB - timeA || String(b.order.id).localeCompare(String(a.order.id), undefined, { numeric: true });
      });
      setOrders(sorted);
    } catch (error) {
      if (seq !== request.current) return;
      setOrdersError(true);
      toast({ title: 'Error', description: error instanceof Error ? error.message : 'Unable to load customer orders.', variant: 'destructive' });
    } finally {
      if (seq === request.current) setLoadingOrders(false);
    }
  }, [customer]);

  useEffect(() => {
    setOrders([]);
    void refreshOrders();
    return () => { ++request.current; };
  }, [refreshOrders]);

  const onOrder = useCallback((message: { body: string }) => {
    try {
      const event = JSON.parse(message.body);
      if (!['NEW_ORDER', 'ORDER_UPDATED'].includes(event.type) || !customer) return;
      const order = event.payload;
      if (String(order?.customerId) === String(customer.id) ||
          orders.some((entry) => String(entry.order.id) === String(order?.id ?? order?.orderId))) {
        void refreshOrders();
      }
    } catch { /* Ignore malformed events. */ }
  }, [customer, orders, refreshOrders]);
  useWebSocketChannel('/topic/orders', onOrder);

  return { customer, setCustomer, orders, loadingOrders, ordersError, refreshOrders };
}
